/**
 * Mengenfeld der freien Position im Fenster „Menge oder Betrag“ (Entwurf „Menge oder Betrag v2“):
 * Stepper [−][Wert][+] und Direkteingabe der Stückzahl über Touch-Ziffernblock oder Hardware-
 * Tastatur. Reine Funktionen ohne React.
 *
 * Die freie Position ist kein Artikel und kein Wiegeartikel: Menge immer ganze Stück, keine
 * Umwandlung Stück ↔ kg, kein Kilopreis. Betrag × Menge und Steuersatz bleiben exakt wie bisher.
 */
import { MAX_VORGABE_MENGE } from './schnellwahl';
import type { ZiffernTaste } from './ziffern';
import type { Position, Steuersatz } from './warenkorb';

/** Kleinste Stückzahl (der Stepper bleibt wie bisher bei 1 stehen). */
export const MENGE_MIN = 1;
/** Größte Stückzahl – dieselbe Grenze wie die Stückzahl für die nächste Kachel. */
export const MENGE_MAX = MAX_VORGABE_MENGE;
/** Höchstens so viele Ziffern nimmt die Direkteingabe an. */
const MAX_STELLEN = String(MENGE_MAX).length;

/** Gültige Stückzahl: ganze Zahl in MENGE_MIN … MENGE_MAX. */
export function istGueltigeMenge(menge: number): boolean {
  return Number.isInteger(menge) && menge >= MENGE_MIN && menge <= MENGE_MAX;
}

/** „−“: eins weniger, nie unter 1. Eine ungültige Ausgangsmenge wird auf die Grenzen gezogen. */
export function mengeVerringern(menge: number): number {
  return begrenzeMenge(menge - 1);
}

/** „+“: eins mehr, nie über MENGE_MAX. */
export function mengeErhoehen(menge: number): number {
  return begrenzeMenge(menge + 1);
}

function begrenzeMenge(menge: number): number {
  if (!Number.isFinite(menge)) {
    return MENGE_MIN;
  }
  return Math.min(MENGE_MAX, Math.max(MENGE_MIN, Math.trunc(menge)));
}

/** Darf der Stepper noch verringern bzw. erhöhen? (Knöpfe sonst gesperrt anzeigen.) */
export function kannVerringern(menge: number): boolean {
  return menge > MENGE_MIN;
}
export function kannErhoehen(menge: number): boolean {
  return menge < MENGE_MAX;
}

/**
 * Text der Direkteingabe → Stückzahl. Nur Ziffern (führende Nullen erlaubt), Ergebnis 1 … 999;
 * leer, 0, Komma/Punkt (keine Bruchteile), Vorzeichen oder zu groß → null.
 */
export function mengeAusEingabe(text: string): number | null {
  const t = text.trim();
  if (!/^[0-9]+$/.test(t)) {
    return null;
  }
  const n = Number(t);
  return istGueltigeMenge(n) ? n : null;
}

/**
 * Zustand der Direkteingabe: `text` ist das, was gerade getippt wurde, `vorher` die Menge beim
 * Öffnen (Abbrechen bzw. eine ungültige Eingabe stellt sie wieder her). `neu` = die erste Taste
 * ersetzt den angezeigten Wert, statt ihn zu verlängern (wie ein markiertes Feld).
 */
export interface MengenEingabe {
  text: string;
  vorher: number;
  neu: boolean;
}

/** Antippen des Werts öffnet die Direkteingabe mit der aktuellen Menge (markiert). */
export function eingabeStarten(menge: number): MengenEingabe {
  const m = istGueltigeMenge(menge) ? menge : MENGE_MIN;
  return { text: String(m), vorher: m, neu: true };
}

/**
 * Taste des Ziffernblocks bzw. der Hardware-Tastatur auf die Direkteingabe anwenden. Ziffern
 * hängen an (die erste ersetzt den markierten Wert), führende Nullen fallen weg, mehr als
 * drei Stellen oder ein Wert über MENGE_MAX werden ignoriert (wie der Betrag bei MAX_CENT);
 * „⌫“ löscht die letzte Ziffer, „C“ leert das Feld.
 */
export function eingabeTaste(e: MengenEingabe, taste: ZiffernTaste): MengenEingabe {
  if (taste === 'C') {
    return { ...e, text: '', neu: false };
  }
  if (taste === '⌫') {
    return { ...e, text: e.neu ? '' : e.text.slice(0, -1), neu: false };
  }
  const basis = e.neu ? '' : e.text;
  const roh = (basis + taste).replace(/^0+/, '');
  if (roh.length > MAX_STELLEN || (roh !== '' && Number(roh) > MENGE_MAX)) {
    return e;
  }
  return { ...e, text: roh, neu: false };
}

/** Übernehmen (Enter/„OK“): gültige Eingabe → neue Menge, sonst bleibt die alte Menge. */
export function eingabeUebernehmen(e: MengenEingabe): number {
  return mengeAusEingabe(e.text) ?? e.vorher;
}

/** Ist die Eingabe übernehmbar? (OK-Taste sonst gesperrt; leer/0 zeigt den Hinweis.) */
export function eingabeGueltig(e: MengenEingabe): boolean {
  return mengeAusEingabe(e.text) !== null;
}

/**
 * Hardware-Tastatur während der Direkteingabe: Ziffern, Rücktaste und Entf wirken auf das Feld,
 * Enter übernimmt, Escape bricht nur die Direkteingabe ab (nicht das Fenster). Andere Tasten
 * (Komma, Punkt, Minus, Buchstaben) werden ignoriert – keine Bruchteile, keine Umwandlung in kg.
 */
export type MengenTaste =
  { art: 'taste'; taste: ZiffernTaste } | { art: 'uebernehmen' } | { art: 'abbrechen' };

export function tastaturZuMengenTaste(key: string): MengenTaste | null {
  if (/^[0-9]$/.test(key)) {
    return { art: 'taste', taste: key as ZiffernTaste };
  }
  switch (key) {
    case 'Backspace':
      return { art: 'taste', taste: '⌫' };
    case 'Delete':
      return { art: 'taste', taste: 'C' };
    case 'Enter':
      return { art: 'uebernehmen' };
    case 'Escape':
      return { art: 'abbrechen' };
    default:
      return null;
  }
}

/**
 * Freie Position aus Betrag (Einzelbetrag in Cent), Menge, Steuersatz und Bezeichnung – exakt
 * wie bisher im Ziffernblock: leere Bezeichnung → „Artikel“, Positionsbetrag = Betrag × Menge.
 * Ohne Betrag oder mit ungültiger Menge entsteht keine Position (null).
 */
export function freiePosition(eingabe: {
  cent: number;
  menge: number;
  steuersatz: Steuersatz;
  bezeichnung: string;
}): Position | null {
  const { cent, menge, steuersatz } = eingabe;
  if (!Number.isInteger(cent) || cent <= 0 || !istGueltigeMenge(menge)) {
    return null;
  }
  const bezeichnung = eingabe.bezeichnung.trim();
  return {
    bezeichnung: bezeichnung === '' ? 'Artikel' : bezeichnung,
    menge,
    bruttoCent: cent * menge,
    steuersatz,
  };
}
